import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components/macro";
import {
  Button,
  Card as MuiCard,
  CardContent,
  CircularProgress,
  Typography,
} from "@material-ui/core";
import { Check, Copy, Download, Share2, UserPlus } from "react-feather";
import QRCode from "qrcode.react";

import { getReferralInvitation } from "../services/referallService";
import { useTranslation } from "react-i18next";

const Card = styled(MuiCard)`
  position: relative;
  z-index: 1;
  margin-top: 28px;
  border-radius: 26px;
  border: 1px solid rgba(148, 163, 184, 0.16);
  box-shadow: 0 24px 50px rgba(15, 23, 42, 0.08);
  background: linear-gradient(135deg, #ffffff 0%, #f0f9ff 55%, #ecfdf5 100%);
`;

const Content = styled(CardContent)`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 28px;
  align-items: center;
  padding: 28px;

  &:last-child {
    padding-bottom: 28px;
  }

  @media (max-width: 760px) {
    grid-template-columns: 1fr;
  }
`;

const Eyebrow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: #0369a1;
  font-size: 0.78rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

const InviteLink = styled.div`
  margin: 18px 0 14px;
  padding: 14px 16px;
  border: 1px solid #bae6fd;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.85);
  color: #0f4c81;
  font-weight: 700;
  overflow-wrap: anywhere;
`;

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
`;

const QrPanel = styled.div`
  min-width: 190px;
  padding: 18px;
  text-align: center;
  border-radius: 20px;
  background: #ffffff;
  border: 1px solid rgba(148, 163, 184, 0.2);

  canvas {
    display: block;
    margin: 0 auto 10px;
  }
`;

function ReferralInvitationCard() {
  const { t, i18n } = useTranslation();
  const [invitation, setInvitation] = useState(null);
  const [error, setError] = useState(false);
  const [copied, setCopied] = useState(false);

  const localizedInvitationUrl = useMemo(() => {
    if (!invitation?.url) return "";
    try {
      const url = new URL(invitation.url, window.location.origin);
      url.searchParams.set("lang", i18n.resolvedLanguage || "es");
      return url.toString();
    } catch (error) {
      const separator = invitation.url.includes("?") ? "&" : "?";
      return `${invitation.url}${separator}lang=${
        i18n.resolvedLanguage || "es"
      }`;
    }
  }, [invitation, i18n.resolvedLanguage]);

  useEffect(() => {
    getReferralInvitation()
      .then(setInvitation)
      .catch(() => setError(true));
  }, []);

  const copyLink = async () => {
    if (navigator.clipboard) {
      await navigator.clipboard.writeText(localizedInvitationUrl);
    } else {
      const input = document.createElement("textarea");
      input.value = localizedInvitationUrl;
      document.body.appendChild(input);
      input.select();
      document.execCommand("copy");
      document.body.removeChild(input);
    }
    setCopied(true);
    window.setTimeout(() => setCopied(false), 2000);
  };

  const downloadQr = () => {
    const canvas = document.getElementById("partner-referral-qr");
    const link = document.createElement("a");
    link.download = `wifiads-invitacion-${invitation.code}.png`;
    link.href = canvas.toDataURL("image/png");
    link.click();
  };

  if (error) {
    return null;
  }

  if (!invitation) {
    return (
      <Card>
        <Content>
          <CircularProgress size={28} />
        </Content>
      </Card>
    );
  }

  const whatsappText = encodeURIComponent(
    t("referrals.whatsappInvitation", { url: localizedInvitationUrl })
  );

  return (
    <Card>
      <Content>
        <div>
          <Eyebrow>
            <UserPlus size={16} /> {t("referrals.invitationCenter")}
          </Eyebrow>
          <Typography variant="h3" style={{ fontWeight: 800, marginTop: 10 }}>
            {t("referrals.invitationTitle")}
          </Typography>
          <Typography color="textSecondary" style={{ marginTop: 8 }}>
            {t("referrals.invitationDescription")}
          </Typography>
          <InviteLink>{localizedInvitationUrl}</InviteLink>
          <Actions>
            <Button
              variant="contained"
              color="primary"
              startIcon={copied ? <Check size={17} /> : <Copy size={17} />}
              onClick={copyLink}
            >
              {copied ? t("referrals.linkCopied") : t("referrals.copyLink")}
            </Button>
            <Button
              variant="outlined"
              startIcon={<Share2 size={17} />}
              href={`https://wa.me/?text=${whatsappText}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              {t("referrals.shareWhatsapp")}
            </Button>
            <Button
              variant="outlined"
              startIcon={<Download size={17} />}
              onClick={downloadQr}
            >
              {t("referrals.downloadQr")}
            </Button>
          </Actions>
        </div>
        <QrPanel>
          <QRCode
            id="partner-referral-qr"
            value={localizedInvitationUrl}
            size={144}
            level="M"
          />
          <Typography variant="body2" style={{ fontWeight: 700 }}>
            {t("referrals.codeValue", { code: invitation.code })}
          </Typography>
          <Typography variant="caption" color="textSecondary">
            {t("referrals.linkedClients", {
              count: invitation.registeredClients,
            })}
          </Typography>
        </QrPanel>
      </Content>
    </Card>
  );
}

export default ReferralInvitationCard;
